import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';
import z from 'zod';
import { useForm } from '../../../hooks/Form';
import { useCurrentUser } from '../../../UserProvider';
import { api, setAuthToken } from '../../../utils/api';

const schema = z.object({
  newEmail: z.string()
    .nonempty('Email is required')
    .email('Invalid email address'),
  password: z.string()
    .nonempty('Password is required')
    .min(8, 'Email must be at least 8 characters')
    .regex(/\d/, 'Email must contain at least one number')
    .regex(/[^A-Za-z0-9]/, 'Email must contain at least one special character')
});

function createInitialValues() {
  return {
    newEmail: '',
    password: ''
  };
}

function createInitialErrors() {
  return {
    newEmail: null,
    password: null
  };
}

export default function EmailResetStart() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useCurrentUser();

  const { isLoading, setIsLoading, error, setError, errors, setErrors, setFieldError, values, setFieldValue } = useForm({
    initialValues: createInitialValues(),
    initialErrors: createInitialErrors()
  });

  /** @param {React.FormEvent<HTMLFormElement>} event */
  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      setError(null);
      setErrors(createInitialErrors());

      setIsLoading(true);

      const result = schema.safeParse(values);

      if (!result.success) {
        result.error.issues.forEach(err => {
          setFieldError(err.path[0], err.message);
        });

        return;
      }

      const { data } = await api.put('/email_resets/finalize', {
        code: searchParams.get('cd'),
        current_email: decodeURIComponent(searchParams.get('ce')),
        new_email: result.data.newEmail,
        password: result.data.password
      }, {
        validateStatus: (status) => status === 200
      });

      setAuthToken(data);

      await refreshUser();

      navigate('/jobs');

    } catch {
      setError('Something went wrong, please try again.');

    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Fragment>
      <form onSubmit={handleSubmit} className='form'>
        <h1 className='form__title'>
          Create a new email
        </h1>
        <p className='form__subtitle'>
          Your new email must be different from your previous email
        </p>
        <div className='form__field-group'>
          <div className='text-field text-field--white form__field'>
            <label htmlFor='new_email' className='text-field__label'>
              New email
            </label>
            <input
              id='new_email'
              name='new_email'
              type='new_email'
              placeholder='• • • • • • • •'
              value={values.newEmail}
              required
              onChange={(e) => setFieldValue('newEmail', e.target.value)}
              className='text-field__input'
            />
            {errors.newEmail && (
              <p className='text-field__error'>
                {errors.newEmail}
              </p>
            )}
          </div>
          <div className='text-field text-field--white form__field'>
            <label htmlFor='password' className='text-field__label'>
              Password
            </label>
            <input
              id='password'
              name='password'
              type='password'
              placeholder='• • • • • • • •'
              value={values.password}
              required
              onChange={(e) => setFieldValue('password', e.target.value)}
              className='text-field__input'
            />
            {errors.password && (
              <p className='text-field__error'>
                {errors.password}
              </p>
            )}
          </div>
        </div>
        {error && (
          <p className='error error--black'>
            {error}
          </p>
        )}
        <div className='form__button-group'>
          <Link className='button button--white button--outline' to='/email-reset' reloadDocument>
            Try again
          </Link>
          <button type='submit' className='button button--white button--fill'>
            {isLoading ? 'Loading...' : 'Reset Email'}
          </button>
        </div>
      </form>
    </Fragment>
  );
}